import React, { useState, useEffect, useRef } from 'react';
import {
  getConsent,
  saveConsent,
  acceptAllConsent,
  CONSENT_CATEGORIES,
  CONSENT_DESCRIPTIONS,
} from '../utils/cookieConsent';

/**
 * Consent Preferences Modal Component
 * Allows users to granularly control their cookie consent preferences
 * Fully accessible with keyboard navigation and ARIA attributes
 */
const ConsentPreferencesModal = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState(CONSENT_CATEGORIES);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    // Load current preferences when modal opens
    if (isOpen) {
      const currentConsent = getConsent();
      if (currentConsent) {
        setPreferences(currentConsent);
      } else {
        setPreferences(CONSENT_CATEGORIES);
      }

      // Focus the close button when modal opens
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    // Handle escape key to close modal
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    // Trap focus within modal
    const handleTabKey = (e) => {
      if (!isOpen) return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleToggle = (category) => {
    // Necessary cookies cannot be toggled
    if (category === 'necessary') return;

    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    onClose();
  };

  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      aria-labelledby="consent-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal container */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in fade-in zoom-in-95 duration-300"
      >
        {/* Decorative gradient bar */}
        <div className="h-2 bg-gradient-to-r from-[#252F4F] via-[#98C454] to-[#252F4F]" />

        {/* Header */}
        <div className="px-8 pt-8 pb-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-[#98C454]/20 to-[#252F4F]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-[#252F4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <div>
                <h2
                  id="consent-modal-title"
                  className="text-2xl font-bold text-[#252F4F] mb-1"
                >
                  Privacy Settings
                </h2>
                <p className="text-sm text-gray-600">
                  Choose what you're comfortable sharing
                </p>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-200/60 transition-all focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2"
              aria-label="Close preferences modal"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Cookie categories */}
          <div className="space-y-4">
            {Object.entries(CONSENT_DESCRIPTIONS).map(([category, info]) => {
              const isEnabled = preferences[category];
              const isNecessary = category === 'necessary';

              return (
                <div
                  key={category}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#252F4F]/30 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-base font-bold text-[#252F4F]">
                          {info.title}
                        </h3>
                        {isNecessary && (
                          <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-0.5 rounded-full">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {info.description}
                      </p>
                    </div>

                    {/* Enhanced Toggle switch */}
                    <button
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${info.title}`}
                      disabled={isNecessary}
                      onClick={() => handleToggle(category)}
                      className={`flex-shrink-0 relative inline-flex h-8 w-16 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2 ${
                        isEnabled
                          ? 'bg-gradient-to-r from-[#252F4F] to-[#1a2136] shadow-lg shadow-[#252F4F]/30'
                          : 'bg-gray-300'
                      } ${isNecessary ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'}`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                          isEnabled ? 'translate-x-9' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleRejectAll}
              className="flex-1 px-5 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              aria-label="Reject all optional cookies"
            >
              Reject All
            </button>

            <button
              onClick={handleSavePreferences}
              className="flex-1 px-5 py-3 text-sm font-semibold text-[#252F4F] bg-[#98C454]/10 border-2 border-[#252F4F] rounded-xl hover:bg-[#98C454]/20 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2"
              aria-label="Save selected preferences"
            >
              Save My Choices
            </button>

            <button
              onClick={handleAcceptAll}
              className="flex-1 px-5 py-3 text-sm font-bold text-white bg-gradient-to-r from-[#252F4F] to-[#1a2136] rounded-xl hover:shadow-lg hover:shadow-[#252F4F]/30 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2"
              aria-label="Accept all cookies"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentPreferencesModal;
